import { useLayoutEffect, useRef, type ComponentProps } from "react";
import { createPortal } from "react-dom";
import type { GroupBase, SelectComponentsConfig } from "react-select";

type MenuPortalProps<Option, IsMulti extends boolean, Group extends GroupBase<Option>> =
  ComponentProps<NonNullable<SelectComponentsConfig<Option, IsMulti, Group>["MenuPortal"]>>;

/** Static menu styles also work when CSP rejects runtime-injected stylesheets. */
export function SelectMenuPortal<Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  appendTo, controlElement, children, innerProps, selectProps,
}: MenuPortalProps<Option, IsMulti, Group>) {
  const portal = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = portal.current;
    if (!host || !controlElement) return;
    let frame = 0;
    const position = () => {
      const rect = controlElement.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop ?? 0;
      const left = viewport?.offsetLeft ?? 0;
      const height = viewport?.height ?? window.innerHeight;
      const width = viewport?.width ?? window.innerWidth;
      const below = top + height - rect.bottom - 12;
      const above = rect.top - top - 12;
      const list = host.querySelector<HTMLElement>(".mzn-select__menu-list");
      const desired = Math.min(selectProps.maxMenuHeight, list?.scrollHeight ?? selectProps.maxMenuHeight);
      const upward = selectProps.menuPlacement === "top" ||
        (selectProps.menuPlacement === "auto" && below < desired && above > below);
      const available = Math.max(40, upward ? above : below);
      const minimumWidth = Number.parseFloat(getComputedStyle(controlElement).getPropertyValue("--select-menu-min-width")) || 0;
      const menuWidth = Math.min(Math.max(rect.width, minimumWidth), width - 16);
      host.style.width = `${menuWidth}px`;
      host.style.left = `${Math.max(left + 8, Math.min(rect.left, left + width - menuWidth - 8))}px`;
      host.style.setProperty("--select-menu-height", `${Math.min(selectProps.maxMenuHeight, available)}px`);
      host.style.top = `${upward ? Math.max(top + 8, rect.top - host.offsetHeight - 6) : rect.bottom + 6}px`;
      host.dir = selectProps.isRtl ? "rtl" : "ltr";
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(position); };
    position();
    const observer = new ResizeObserver(schedule);
    observer.observe(controlElement);
    observer.observe(host);
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("resize", schedule);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [controlElement, selectProps.maxMenuHeight, selectProps.menuPlacement, selectProps.isRtl, children]);
  if (!appendTo) return null;
  return createPortal(<div {...innerProps} ref={portal} data-dropdown-owner={selectProps.inputId} className="mzn-select__menu-portal mzn-select-static-portal">{children}</div>, appendTo);
}
